"use client";

import * as React from "react";
import { useEffect, useMemo } from "react";
import {
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  useAuiState,
} from "@assistant-ui/react";
import {
  ArchiveIcon,
  MoreHorizontalIcon,
  PinIcon,
  PinOffIcon,
  XIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useThreadActions } from "@/components/custom/providers/customThreadListProvider";
import { useThreadListContext } from "@/components/custom/contexts/threadContext";
import { useRouteContext } from "@/components/custom/contexts/routeContext";
import { cn } from "@/lib/utils";
import { appendLocParam } from "@/lib/i18n";
import { useBackgroundStreamingPoll } from "@/hooks/use-background-streaming-poll";
import { useThreadListStore } from "@/components/custom/stores/threadListStore";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const MAX_RECENTS = 20;

/** Context providing the set of thread IDs currently streaming in the background. */
const StreamingThreadsContext = React.createContext<Set<string>>(new Set());

export const ThreadList: React.FC = () => {
  const { t } = useTranslation();
  const { isListLoading, threadItems } = useThreadListContext();
  const threadIds = useAuiState(s => s.threads.threadIds);
  const streamingThreadIds = useBackgroundStreamingPoll();

  // Sync the per-item Zustand store so ThreadListItem can subscribe to just its own slice.
  useEffect(() => {
    useThreadListStore.getState().syncItems(
      threadItems.map(ti => ({ remoteId: ti.remoteId, isPinned: ti.isPinned ?? false })),
    );
  }, [threadItems]);

  useEffect(() => {
    useThreadListStore.getState().syncStreaming(streamingThreadIds);
  }, [streamingThreadIds]);

  // Map remoteId → ThreadItem for O(1) pinned-flag lookup. threadIds drives the
  // index ordering AUI exposes to ItemByIndex; we filter that index space into
  // two disjoint groups (pinned, recents) and render each group separately so
  // the section headers can never fall on the wrong row.
  const threadItemMap = useMemo(
    () => new Map(threadItems.map(ti => [ti.remoteId, ti])),
    [threadItems],
  );

  // Server returns threads by UpdatedAt DESC (pure recency, see IThreadRepository).
  // Within each filtered group, preserve threadIds order — so the most recently
  // bumped thread surfaces at position 0 of its section the moment it's reloaded.
  const pinnedIndices = useMemo(
    () =>
      threadIds
        .map((id, i) => ({ id, i }))
        .filter(({ id }) => threadItemMap.get(id)?.isPinned)
        .map(({ i }) => i),
    [threadIds, threadItemMap],
  );

  const recentsIndices = useMemo(
    () =>
      threadIds
        .map((id, i) => ({ id, i }))
        .filter(({ id }) => !threadItemMap.get(id)?.isPinned)
        .map(({ i }) => i)
        .slice(0, MAX_RECENTS),
    [threadIds, threadItemMap],
  );

  return (
    <StreamingThreadsContext.Provider value={streamingThreadIds}>
      <ThreadListPrimitive.Root className="aui-thread-list-root flex flex-col gap-0.5">
        {isListLoading && <ThreadListSkeleton />}
        {pinnedIndices.length > 0 && (
          <>
            <div className={SECTION_HEADER_CLASSES}>{t("sidebar.pinned")}</div>
            {pinnedIndices.map((index) => (
              <ThreadListPrimitive.ItemByIndex
                key={`pinned-${index}`}
                index={index}
                components={{ ThreadListItem }}
              />
            ))}
          </>
        )}
        {recentsIndices.length > 0 && (
          <>
            <div className={SECTION_HEADER_CLASSES}>{t("sidebar.recents")}</div>
            {recentsIndices.map((index) => (
              <ThreadListPrimitive.ItemByIndex
                key={`recents-${index}`}
                index={index}
                components={{ ThreadListItem }}
              />
            ))}
          </>
        )}
      </ThreadListPrimitive.Root>
    </StreamingThreadsContext.Provider>
  );
};

const SKELETON_WIDTHS = ["80%", "55%", "70%", "60%", "45%"];

const ThreadListSkeleton: React.FC = () => (
  <div className="flex flex-1 flex-col items-center justify-center gap-1 py-4">
    {SKELETON_WIDTHS.map((width, i) => (
      <div
        key={i}
        className="flex h-9 w-full items-center gap-2 rounded-lg px-1"
      >
        <span className="flex size-3 shrink-0" />
        <Skeleton
          className="h-4"
          style={{ width, animationDelay: `${i * 100}ms` }}
        />
      </div>
    ))}
  </div>
);

const SECTION_HEADER_CLASSES =
  "praetor-sidebar-section mt-3.5 px-2.5 pb-1 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground";

const ThreadListItem: React.FC = () => {
  const { t } = useTranslation();
  const remoteId = useAuiState(state => state.threadListItem.remoteId);
  const isActive = useAuiState(
    s => s.threads.mainThreadId === s.threadListItem.id,
  );

  // Per-item store subscription — only re-renders when THIS item's data changes.
  const itemData = useThreadListStore(s => remoteId ? s.items[remoteId] : undefined);
  const isPinned = itemData?.isPinned ?? false;
  const isStreaming = itemData?.isStreaming ?? false;

  return (
    <>
      <ThreadListItemPrimitive.Root
        className={cn(
          "praetor-thread-row group relative flex items-center gap-2.5 overflow-hidden rounded-lg px-2.5 py-1.5 text-[13.5px] transition-colors duration-120",
          // Active and hover land on the same tint — visible enough that the
          // selected row reads as "lit" without being loud, and the user
          // doesn't perceive a tint shift when sliding the cursor over the
          // currently-active row.
          isActive
            ? "bg-accent text-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
        )}
      >
        <ThreadListItemPrimitive.Trigger
          aria-label={t("threadList.newConversation")}
          className="praetor-thread-row-trigger absolute inset-0 size-full cursor-pointer"
        />
        {isStreaming && !isActive && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="pointer-events-none absolute left-1 top-1/2 size-2 -translate-y-1/2 rounded-full bg-blue-500 animate-pulse"
                aria-label={t("thread.backgroundActive")}
              />
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">
              {t("thread.backgroundActive")}
            </TooltipContent>
          </Tooltip>
        )}
        <span
          className="pointer-events-none relative flex min-w-0 flex-1 items-center truncate text-start"
        >
          <ThreadListItemPrimitive.Title
            fallback={t("threadList.newConversation")}
          />
        </span>
        <div className="relative">
          <ThreadRowMenu remoteId={remoteId} isPinned={isPinned} />
        </div>
      </ThreadListItemPrimitive.Root>
    </>
  );
};

function ThreadRowMenu({
  remoteId,
  isPinned,
}: {
  remoteId: string | undefined;
  isPinned: boolean;
}) {
  const { t } = useTranslation();
  const { pin, unpin, delete: deleteThread } = useThreadActions();
  const navigate = useNavigate();
  const routeContext = useRouteContext();

  const handlePinToggle = React.useCallback(() => {
    if (!remoteId) return;
    if (isPinned) unpin(remoteId);
    else pin(remoteId);
  }, [remoteId, isPinned, pin, unpin]);

  const handleDelete = React.useCallback(async () => {
    if (!remoteId) return;
    await deleteThread(remoteId);
    // Navigate away if the deleted thread was the active one.
    if (routeContext.threadId === remoteId) {
      const dest = routeContext.caseId ?? "default";
      navigate(appendLocParam(`/${dest}`));
    }
  }, [remoteId, deleteThread, routeContext.threadId, routeContext.caseId, navigate]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("threadList.moreOptions")}
          className="praetor-thread-row-more flex size-[22px] cursor-pointer items-center justify-center rounded-[5px] text-muted-foreground opacity-0 transition-opacity duration-120 hover:bg-border hover:text-foreground group-hover:opacity-100 data-[state=open]:opacity-100 focus-visible:opacity-100"
        >
          <MoreHorizontalIcon className="size-3.5" strokeWidth={1.7} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        className="praetor-thread-row-menu min-w-[180px]"
      >
        {remoteId && (
          <DropdownMenuItem onClick={handlePinToggle}>
            {isPinned ? (
              <PinOffIcon className="size-4" strokeWidth={1.7} />
            ) : (
              <PinIcon className="size-4" strokeWidth={1.7} />
            )}
            {isPinned ? t("threadList.unpin") : t("threadList.pin")}
          </DropdownMenuItem>
        )}
        <ThreadListItemPrimitive.Archive asChild>
          <DropdownMenuItem>
            <ArchiveIcon className="size-4" strokeWidth={1.7} />
            {t("threadList.archive")}
          </DropdownMenuItem>
        </ThreadListItemPrimitive.Archive>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={handleDelete}>
          <XIcon className="size-4" strokeWidth={1.7} />
          {t("threadList.delete")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** @internal Exported for unit testing only. */
export { ThreadListItem as _ThreadListItem, StreamingThreadsContext as _StreamingThreadsContext };
